import { useState, useEffect, useCallback } from 'react';
import { useHydration } from './useHydration';

/**
 * Custom hook to detect mobile/desktop device types with debounced resize handling
 * @returns {string} 'mobile' or 'desktop'
 */
export function useDeviceType() {
    // Use hydration hook to prevent hydration mismatch
    const isHydrated = useHydration();
    // Initialize with actual screen width to prevent flash
    // const [deviceType, setDeviceType] = useState(() => {
    //     if (typeof window !== 'undefined') {
    //         return window.innerWidth <= 992 ? 'mobile' : 'desktop';
    //     }
    //     return 'undefined'; // fallback for SSR
    // });
    
    const [deviceType, setDeviceType] = useState('desktop'); // Default to desktop for SSR

    // Debounce function to limit resize event frequency
    const debounce = useCallback((func, delay) => {
        let timeoutId;
        return (...args) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => func.apply(null, args), delay);
        };
    }, []);

    useEffect(() => {
        if (!isHydrated) return;

        const getDeviceType = (width) => {
            return width <= 992 ? 'mobile' : 'desktop';
        };

        const handleResize = () => {
            const width = window.innerWidth;
            const newDeviceType = getDeviceType(width);
            
            // Only update state if device type actually changed
            setDeviceType(prevType => {
                return prevType !== newDeviceType ? newDeviceType : prevType;
            });
        };

        // Debounced resize handler (150ms delay for optimal performance)
        const debouncedHandleResize = debounce(handleResize, 150);

        // Check on mount (immediate, no debounce needed)
        handleResize();

        // Add debounced event listener
        window.addEventListener('resize', debouncedHandleResize);

        // Cleanup
        return () => {
            window.removeEventListener('resize', debouncedHandleResize);
        };
    }, [isHydrated, debounce]);

    return deviceType;
}
